import {
  useDrawingContext,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { ReactNode } from 'react';

import { ShipSelectionProvider } from './contexts/ShipSelectionContext';
import { TargetChooserProvider } from './contexts/TargetChooserContext';
import { CursorPositionProvider } from './contexts/CursorPositionContext';
import { MapOverlayProvider } from './contexts/MapOverlayContext';
import { RemoteZoomPulseProvider } from './contexts/RemoteZoomPulseContext';
import { AISMapLayer } from './AISMapLayer';
import { ListenForShipClicks } from './ListenForShipClicks';
import { MapPanel } from './MapPanel/MapPanel';
import { useDrawings } from './useDrawings';
import { useSettings } from '../Settings/SettingsProvider';
import { useZoomAndSelectShip } from './useZoomAndSelectShip';
import { PortLayer } from './PortLayer';
import { ListenForLayerClicks } from './ListenForLayerClicks';
import {
  useSelectedGpsLostGridCellStyling,
  useSelectedPortStyling,
  useSelectedSourceIdentifiersStyling,
} from './useSelectedStyling';
import { CoverageLayer } from './Coverage/CoverageLayer';
import { RadarOverlayLayers } from '../RadarOverlay/RadarOverlayLayers';
import { useCursorCoordinates } from './useCursorCoordinates';
import { useCheckSearchAndRescue } from '../../../../../libs/websockets/src/lib/ol-server/useCheckSearchAndRescue';
import { TargetsOnMapProvider } from './TargetsOnMapContext';
import { HoverMap } from './HoverMap';
import { AtonProvider } from '../Atons/AtonContext';
import { RoutesProvider } from '../Routes/RoutesContext';
import { useZoomToExternalShip } from './useZoomToExternalShip';
import { usePlayReplay } from '../Replay/usePlayReplay';
import { GPSLostLayer } from './GpsLost/GPSLostLayer';
import { useOnMapStart } from './onMapStartup/useOnMapStart';
import { usePopupEventHandler } from '../Popup';
import { usePopupDataProviders } from '../Popup/usePopupDataProviders';
import { PortTimelineDataProvider } from '../Popup/PortTimelineDataProvider';
import { ArrivalsDataProvider } from '../Popup/ArrivalsDataProvider';
import { ReportShipDataProvider } from '../Popup/ReportShipDataProvider';
import { ReportDataProvider } from '../Popup/ReportDataProvider';
import { EventsDataProvider } from '../Popup/EventsDataProvider';
import { VesselTimelineDataProvider } from '../Popup/VesselTimelineDataProvider';
import { TargetsDataProvider } from '../Popup/TargetsDataProvider';
import { EventsProvider } from '../Events/EventsContext';
import { useFocusEventNavigation } from '../Events/useFocusEventNavigation';
import {
  MapContextMenuProvider,
  MapContextMenu,
  ListenForContextMenu,
} from './ContextMenu';
import { useTrackHoverTooltip } from './hooks/useTrackHoverTooltip';
import { usePointHoverTooltip } from './hooks/usePointHoverTooltip';
import { useTrackTimeChangeListener } from './TargetsOnMap/useShowAllTracks';

export interface MapComponentsProps {
  children?: ReactNode;
}

export interface AISComponentsProps {
  children?: ReactNode;
}

function AISComponents({ children }: AISComponentsProps) {
  const drawingContext = useDrawingContext();
  const { map } = useOpenLayersContext();
  useZoomAndSelectShip();
  useCursorCoordinates();
  useZoomToExternalShip();
  usePlayReplay();
  usePopupEventHandler();
  usePopupDataProviders();
  useTrackHoverTooltip();
  usePointHoverTooltip();
  useTrackTimeChangeListener();

  const { settings } = useSettings();
  useCheckSearchAndRescue(
    Boolean(settings['ribbonPlugin.settings']?.emergency_alerts_mdm),
  );
  useSelectedPortStyling();
  useSelectedSourceIdentifiersStyling();
  useSelectedGpsLostGridCellStyling();
  useFocusEventNavigation();

  return (
    <>
      <AISMapLayer />
      <PortLayer />
      <CoverageLayer />
      <RadarOverlayLayers />
      <GPSLostLayer />
      {/* Popup data providers - handle requests from popup windows */}
      <PortTimelineDataProvider />
      <ArrivalsDataProvider />
      <ReportShipDataProvider />
      <ReportDataProvider />
      <VesselTimelineDataProvider />
      <TargetsDataProvider />
      {/* EventsProvider wraps components that need shared events data */}
      <EventsProvider>
        {/* EventsDataProvider responds to popup init requests using EventsContext data */}
        <EventsDataProvider />
        {!drawingContext.geometryType && (
          <>
            <ListenForShipClicks></ListenForShipClicks>
            <ListenForLayerClicks map={map}></ListenForLayerClicks>
            <ListenForContextMenu map={map}></ListenForContextMenu>
            <HoverMap />
          </>
        )}
        <MapPanel></MapPanel>
      </EventsProvider>
      {children}
    </>
  );
}

export function MapComponents({ children }: MapComponentsProps) {
  // This hook takes care of everything that's drawn in MDM
  useDrawings();
  useOnMapStart();

  return (
    <TargetChooserProvider>
      <CursorPositionProvider>
        <MapOverlayProvider>
          <ShipSelectionProvider>
            <RemoteZoomPulseProvider>
              <AtonProvider>
                <TargetsOnMapProvider>
                  <RoutesProvider>
                    <MapContextMenuProvider>
                      <AISComponents>{children}</AISComponents>
                      <MapContextMenu />
                    </MapContextMenuProvider>
                  </RoutesProvider>
                </TargetsOnMapProvider>
              </AtonProvider>
            </RemoteZoomPulseProvider>
          </ShipSelectionProvider>
        </MapOverlayProvider>
      </CursorPositionProvider>
    </TargetChooserProvider>
  );
}
